import styled, { css } from "styled-components";
import { Button as ButtonPrimitive } from "react-aria-components";
import { CoreButton } from "@boi/components-core";
import { useDesignTokens, parseSizeToken } from "@boi/react-design-tokens";

type ButtonProps = {
  $variant: CoreButton.Variant;
  $fullWidth?: boolean;
};

export const Button = styled(ButtonPrimitive)<ButtonProps>`
  ${({ $variant, $fullWidth }) => {
    const { tokens } = useDesignTokens();
    const { button } = tokens;

    return css`
      all: unset;
      position: relative;
      display: grid;
      align-items: start;
      height: ${button.height};
      width: ${$fullWidth ? "100%" : "auto"};
      min-width: ${button.minWidth};
      cursor: pointer;

      &[data-disabled] {
        cursor: not-allowed;
        opacity: ${button.opacity.disabled};
      }

      &[aria-disabled] {
        cursor: not-allowed;
      }

      &[data-focus-visible]:not([data-pending]) {
        border-radius: ${button.topLayer.stroke.radius};
        outline-width: ${button.focusRing.width};
        outline-style: ${button.focusRing.style};
        outline-color: ${button.focusRing.color[$variant]};
        outline-offset: ${button.focusRing.offset};
      }
    `;
  }}
`;

type TopLayerProps = {
  $variant: CoreButton.Variant;
};

export const TopLayer = styled.div<TopLayerProps>`
  ${({ $variant }) => {
    const { tokens } = useDesignTokens();
    const { button } = tokens;

    return [
      css`
        position: relative;
        overflow: hidden;
        color: ${button.topLayer.color[$variant].base};
        background: ${button.topLayer.backgroundColor[$variant].base};
        height: ${button.topLayer.height[$variant]};
        padding-left: ${button.topLayer.padding[$variant].left};
        padding-right: ${button.topLayer.padding[$variant].right};
        border-width: ${button.topLayer.stroke.width};
        border-style: ${button.topLayer.stroke.style};
        border-color: ${button.topLayer.stroke.color[$variant].base};
        border-radius: ${button.topLayer.stroke.radius};
        user-select: none;

        transform: translateY(0px);
        transition: transform ${button.topLayer.animationDuration}ms;

        ${Button}[data-hovered] && {
          color: ${button.topLayer.color[$variant].hover};
          background: ${button.topLayer.backgroundColor[$variant].hover};
          transform: translateY(${button.topLayer.translateY[$variant].hover});
        }

        ${Button}[data-pressed] && {
          color: ${button.topLayer.color[$variant].active};
          background: ${button.topLayer.backgroundColor[$variant].active};
          transform: translateY(${button.topLayer.translateY[$variant].active});
        }

        ${Button}[data-pending] && {
          color: ${button.topLayer.color[$variant].active};
          border-color: ${button.topLayer.stroke.color[$variant].active};
          background: ${button.topLayer.backgroundColor[$variant].active};
          transform: translateY(${button.topLayer.translateY[$variant].active});
        }

        ${Button}[data-pending][data-hovered] && {
          color: ${button.topLayer.color[$variant].active};
          border-color: ${button.topLayer.stroke.color[$variant].active};
          background: ${button.topLayer.backgroundColor[$variant].active};
          transform: translateY(${button.topLayer.translateY[$variant].active});
        }

        ${Button}[data-pending][data-pressed] && {
          color: ${button.topLayer.color[$variant].active};
          border-color: ${button.topLayer.stroke.color[$variant].active};
          background: ${button.topLayer.backgroundColor[$variant].active};
          transform: translateY(${button.topLayer.translateY[$variant].active});
        }

        ${Button}[data-pending][data-focus-visible] && {
          border-radius: ${button.topLayer.stroke.radius};
          outline-width: ${button.focusRing.width};
          outline-style: ${button.focusRing.style};
          outline-color: ${button.focusRing.color[$variant]};
          outline-offset: ${button.focusRing.offset};
        }
      `,
      $variant === "secondary" &&
        css`
          ${Button}[data-hovered] && {
            border-color: ${button.topLayer.stroke.color[$variant].hover};
          }

          ${Button}[data-pressed] && {
            border-color: ${button.topLayer.stroke.color[$variant].active};
          }
        `,
    ];
  }}
`;

type BottomLayerProps = {
  $variant: CoreButton.Variant;
};

export const BottomLayer = styled.div<BottomLayerProps>`
  ${({ $variant }) => {
    const { tokens } = useDesignTokens();
    const { button } = tokens;
    const { shadow } = button.bottomLayer;

    return css`
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: ${button.bottomLayer.height};
      border-radius: ${button.bottomLayer.radius};
      background: ${button.bottomLayer.backgroundColor[$variant].base};
      box-shadow: 0px ${shadow.blur} ${shadow.offsetY} ${shadow.color};

      ${Button}[data-hovered] && {
        background: ${button.bottomLayer.backgroundColor[$variant].hover};
      }

      ${Button}[data-pressed] && {
        background: ${button.bottomLayer.backgroundColor[$variant].active};
        box-shadow: none;
      }

      ${Button}[data-pending] && {
        background: ${button.bottomLayer.backgroundColor[$variant].active};
        box-shadow: none;
      }

      ${Button}[data-pending][data-hovered] && {
        background: ${button.bottomLayer.backgroundColor[$variant].active};
      }

      ${Button}[data-pending][data-pressed] && {
        background: ${button.bottomLayer.backgroundColor[$variant].active};
      }
    `;
  }}
`;

export const Content = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 100%;
  overflow: hidden;
`;

export const IconWrapper = styled.span`
  ${() => {
    const { tokens } = useDesignTokens();
    const { button } = tokens;

    const width = parseSizeToken(button.topLayer.icon.width);
    const height = parseSizeToken(button.topLayer.icon.width);

    return css`
      display: flex;
      flex-shrink: 0;

      ${Button}[data-pending] && {
        visibility: hidden;
      }

      svg {
        width: ${width};
        height: ${height};
      }
    `;
  }}
`;

export const TypographyWrapper = styled.div`
  overflow: hidden;

  ${Button}[data-pending] && {
    visibility: hidden;
  }
`;

export const SpinnerWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
`;
